import _findIndex from 'lodash.findindex';
import { defineStore } from 'pinia';

import { useStore as useShapeStore } from './shape';
import { useStore as useColorManagementStore } from './colorsManagement';

import { useStore as usePriceSectionStore } from './priceSection';
import { useStore as useShapeBoxSectionStore } from './shapeBoxSection';
import { useStore as useProductStore } from './product';
import { useStore as useNotificationStore } from './notification';
import { useStore as useHistoryStore } from './history';
import { useStore as useMirrorStore } from './mirror';

import { mm2px, recalculateDPI } from '@/utils';
import { PmsColor } from '@midocean/webapps-shared-domain';
import { base64StringToBlob } from 'blob-util';
import { determineNewHeight, determineNewWidth, px2mm } from '@/utils/common';
import { createStateSnapshot } from '@/utils/historyHelper';

export const useStore = defineStore({
  id: 'section',

  state: () => {
    return {
      sections: [
        {
          id: 'print-position-box-section',
          component: 'print-position-box',
          context: ['any'],
        },
        {
          id: 'ruler-settings',
          component: 'ruler-settings',
          context: ['any'],
        },
        {
          id: 'color-management',
          component: 'color-management',
          context: ['any'],
        },

        {
          id: 'shapebox-size-section',
          component: 'shape-box-size',
          context: ['shape-manipulation'],
        },
        {
          id: 'background-type-section',
          component: 'background-type-selection',
          context: ['shape-manipulation'],
        },
        {
          id: 'price-calc-section',
          component: 'price-calc',
          context: ['any'],
        },
        {
          id: 'confirm-and-order-section',
          component: 'confirm-and-order',
          context: ['any'],
        },
      ],
      appContext: ['any'],
    };
  },

  getters: {
    activeSections(state) {
      return state.sections.filter((section) =>
        state.appContext.some((appCtx) => section.context.includes(appCtx))
      );
    },

    activeSectionsData() {
      const priceSectionStore = usePriceSectionStore();
      const colorManagmentStore = useColorManagementStore();
      const shapeBoxSectionStore = useShapeBoxSectionStore();

      return {
        'shapebox-size-section': shapeBoxSectionStore.propsData,
        'section-b': {},
        'confirm-and-order-section': {},
        'price-calc-section': {
          eventPayload: priceSectionStore.prepareEventPayload(),
        },
        'color-management': {
          pmsColorsInUse: colorManagmentStore.getPmsColorsOnPrintPosition,
        },
      };
    },
  },

  actions: {
    async updateIsRoundLogo(value: boolean) {
      const shapeStore = useShapeStore();
      const { activeShapeId, shapes } = shapeStore;
      const currentShapeIndex = _findIndex(shapes, { id: activeShapeId });
      const activeShape = shapes[currentShapeIndex];

      await shapeStore.makeLogoRound(currentShapeIndex);
      await shapeStore.updateColorsAfterImageIsAdjusted(activeShapeId, false);

      activeShape.isRoundLogo = value;
    },
    update(key: string, value: number) {
      const shapeStore = useShapeStore();
      const notificationStore = useNotificationStore();
      const productStore = useProductStore();
      const positionImageDpi =
        productStore.selectedPrintPosition.originalImageWebDpi;
      const { activeShapeId, shapes } = shapeStore;

      const currentShapeIndex = _findIndex(shapes, { id: activeShapeId });

      const activeShape = shapes[currentShapeIndex];
      const isTextShape = activeShape.kind === 'Text';

      if (isTextShape) {
        const smallDetails = activeShape.smallDetailsResult
          ? { ...activeShape.smallDetailsResult, isCheckValid: false }
          : undefined;
        shapeStore.$patch((state) => {
          state.shapes.splice(currentShapeIndex, 1, {
            ...state.shapes[currentShapeIndex],
            [key]: mm2px(value, positionImageDpi),
            smallDetailsResult: smallDetails,
          });
        });
      }

      if (!isTextShape && key === 'width') {
        const newHeight = determineNewHeight(
          activeShape.height as number,
          activeShape.width as number,
          mm2px(value, positionImageDpi)
        );

        const smallDetails = activeShape.smallDetailsResult
          ? { ...activeShape.smallDetailsResult, isCheckValid: false }
          : undefined;

        if (currentShapeIndex > -1) {
          shapeStore.$patch((state) => {
            state.shapes.splice(currentShapeIndex, 1, {
              ...state.shapes[currentShapeIndex],
              width: mm2px(value, positionImageDpi),
              height: newHeight,
              smallDetailsResult: smallDetails,
            });

            const dpi = productStore.activePosition.originalImageWebDpi || 300;
            const originalShape = state.shapes[currentShapeIndex];

            if (originalShape.kind === 'Logo') {
              const newHDPI = recalculateDPI(
                value,
                originalShape.originalHorizontalResolutionDPIs,
                originalShape.originalWidthMM
              );

              const newVDPI = recalculateDPI(
                px2mm(newHeight as number, dpi),
                originalShape.originalVerticalResolutionDPIs,
                originalShape.originalHeightMM
              );

              const affectedShape = {
                ...state.shapes[currentShapeIndex],
                width: mm2px(value, positionImageDpi),
                height: newHeight,
                pngHorizontalResolutionDPIs: Math.round(newHDPI),
                pngVerticalResolutionDPIs: Math.round(newVDPI),
                smallDetailsResult: smallDetails,
              };
              const printPosition =
                productStore.data.productPrintPositions.find(
                  (p) => p.id === productStore.selectedPrintPosition.id
                );
              const technique = printPosition?.printTechniques.find(
                (t) =>
                  t.id === productStore.selectedPrintPosition.techniqueId ||
                  t.printTechnique.id ===
                    productStore.selectedPrintPosition.techniqueId
              );
              const config = technique?.logoValidators.find(
                (v) => v.type.trim() === 'Resolution'
              )?.configuration;

              if (
                config &&
                affectedShape &&
                affectedShape.kind === 'Logo' &&
                !affectedShape.isVectorFileTrueVectorFile
              ) {
                notificationStore.revalidateResolutionOnly(
                  affectedShape,
                  config
                );
              }
            }
          });
        }
      }

      if (!isTextShape && key === 'height') {
        const newWidth = determineNewWidth(
          activeShape.height as number,
          activeShape.width as number,
          mm2px(value, positionImageDpi)
        );

        const smallDetails = activeShape.smallDetailsResult
          ? { ...activeShape.smallDetailsResult, isCheckValid: false }
          : undefined;

        if (currentShapeIndex > -1) {
          shapeStore.$patch((state) => {
            state.shapes.splice(currentShapeIndex, 1, {
              ...state.shapes[currentShapeIndex],
              height: mm2px(value, positionImageDpi),
              width: newWidth,
              smallDetailsResult: smallDetails,
            });

            const dpi = productStore.activePosition.originalImageWebDpi || 300;
            const originalShape = state.shapes[currentShapeIndex];

            if (originalShape.kind === 'Logo') {
              const newHDPI = recalculateDPI(
                px2mm(newWidth, dpi),
                originalShape.originalHorizontalResolutionDPIs,
                originalShape.originalWidthMM
              );

              const newVDPI = recalculateDPI(
                value,
                originalShape.originalVerticalResolutionDPIs,
                originalShape.originalHeightMM
              );

              const affectedShape = {
                ...state.shapes[currentShapeIndex],
                width: newWidth,
                height: mm2px(value, positionImageDpi),
                pngHorizontalResolutionDPIs: Math.round(newHDPI),
                pngVerticalResolutionDPIs: Math.round(newVDPI),
                smallDetailsResult: smallDetails,
              };
              const printPosition =
                productStore.data.productPrintPositions.find(
                  (p) => p.id === productStore.selectedPrintPosition.id
                );
              const technique = printPosition?.printTechniques.find(
                (t) =>
                  t.id === productStore.selectedPrintPosition.techniqueId ||
                  t.printTechnique.id ===
                    productStore.selectedPrintPosition.techniqueId
              );
              const config = technique?.logoValidators.find(
                (v) => v.type.trim() === 'Resolution'
              )?.configuration;

              if (
                config &&
                affectedShape &&
                affectedShape.kind === 'Logo' &&
                !affectedShape.isVectorFileTrueVectorFile
              ) {
                notificationStore.revalidateResolutionOnly(
                  affectedShape,
                  config
                );
              }
            }
          });
        }
      }

      if (key === 'rotation') {
        const rotation = (value * Math.PI) / 180;
        shapeStore.$patch((state) => {
          state.shapes.splice(currentShapeIndex, 1, {
            ...state.shapes[currentShapeIndex],
            rotation,
          });
        });
      }

      const mirrorStore = useMirrorStore();
      mirrorStore.updateMirrorShape(shapeStore.shapes[currentShapeIndex]);

      const historyStore = useHistoryStore();
      const snapshot = createStateSnapshot();
      historyStore.recordHistory(snapshot);
    },
    async replaceColor(from: PmsColor, to: PmsColor) {
      const shapeStore = useShapeStore();
      const mirrorStore = useMirrorStore();
      const productStore = useProductStore();

      await shapeStore.replaceColor(from, to).then((data) => {
        data?.forEach(async (x) => {
          const shapeIndex = _findIndex(shapeStore.shapes, {
            id: x.id,
          });
          const shape = shapeStore.shapes[shapeIndex];

          if (shape.kind === 'Logo') {
            const artworkPmsColors = [...shape.pmsColorsInPngFile];
            to.colorFuzz = 1;

            const artworkPmsColorsWithSameName = artworkPmsColors.filter((x) =>
              x.name.includes(from.name)
            );

            artworkPmsColorsWithSameName.forEach((artworkPmsColor) => {
              artworkPmsColor.name = to.name;
              artworkPmsColor.originalName = to.name;
              artworkPmsColor.red = to.red;
              artworkPmsColor.green = to.green;
              artworkPmsColor.blue = to.blue;
              artworkPmsColor.isOriginalSpotColor = from.isOriginalSpotColor;
              artworkPmsColor.colorFuzz = to.colorFuzz;
            });

            shapeStore.$patch((state) => {
              const originalShape = state.shapes[shapeIndex];
              if (originalShape.kind === 'Logo') {
                state.shapes.splice(shapeIndex, 1, {
                  ...originalShape,
                  pngFile: base64StringToBlob(x.file, 'image/png'),
                  pngFileWithoutConverters: base64StringToBlob(
                    x.file,
                    'image/png'
                  ),
                  pmsColorsInPngFile: artworkPmsColors,
                  backgroundImage: URL.createObjectURL(
                    base64StringToBlob(x.file, 'image/png')
                  ),
                });
              }
            });

            if (
              productStore.selectedPrintPosition.techniqueId &&
              shapeStore.shapes[shapeIndex]
            ) {
              await shapeStore.applyEffectIfNeeded(
                productStore.selectedPrintPosition.id,
                productStore.selectedPrintPosition.techniqueId,
                shapeStore.shapes[shapeIndex]
              );
            }

            mirrorStore.updateMirrorShape(shapeStore.shapes[shapeIndex]);
          }
        });
      });
    },
    async removeColor(color: PmsColor) {
      const shapeStore = useShapeStore();
      const mirrorStore = useMirrorStore();
      const productStore = useProductStore();

      await shapeStore.removeColor(color).then((data) => {
        data?.forEach(async (x) => {
          const shapeIndex = _findIndex(shapeStore.shapes, {
            id: x.id,
          });

          const originalShape = { ...shapeStore.shapes[shapeIndex] };
          if (originalShape.kind === 'Logo') {
            const shapePmsColors = originalShape.pmsColorsInPngFile;

            if (shapePmsColors.length === 1) {
              // NOTE: Notifications for bell and all validator pipeline results are left hanging even though nothing exists on Pintura

              // If it's the last colour then the artwork is removed
              await shapeStore.removeArtworkPayload(x.id, false);
            } else {
              if (shapePmsColors) {
                const newPmsColors = shapePmsColors.filter(
                  (x) => !x.name.includes(color.name)
                );

                const smallDetails =
                  shapeStore.shapes[shapeIndex].smallDetailsResult;

                shapeStore.$patch((state) => {
                  const widthScaleFactor =
                    originalShape.pngWidthPixels / x.file.widthInPx;
                  const heightScaleFactor =
                    originalShape.pngHeightPixels / x.file.heightInPx;
                  const newWidth = originalShape.width / widthScaleFactor;
                  const newHeight = originalShape.height / heightScaleFactor;

                  state.shapes.splice(shapeIndex, 1, {
                    ...originalShape,
                    pngFile: base64StringToBlob(x.file.pngFile, 'image/png'),
                    backgroundImage: URL.createObjectURL(
                      base64StringToBlob(x.file.pngFile, 'image/png')
                    ),
                    pngFileWithoutConverters: base64StringToBlob(
                      x.file.pngFile,
                      'image/png'
                    ),
                    pngWidthPixels: x.file.widthInPx,
                    pngHeightPixels: x.file.heightInPx,
                    aspectRatio: x.file.widthInPx / x.file.heightInPx,
                    width: newWidth,
                    height: newHeight,
                    pmsColorsInPngFile: newPmsColors,
                    smallDetailsResult: smallDetails
                      ? { ...smallDetails, isCheckValid: false }
                      : undefined,
                  });
                });

                if (
                  productStore.selectedPrintPosition.techniqueId &&
                  shapeStore.shapes[shapeIndex]
                ) {
                  await shapeStore.applyEffectIfNeeded(
                    productStore.selectedPrintPosition.id,
                    productStore.selectedPrintPosition.techniqueId,
                    shapeStore.shapes[shapeIndex]
                  );
                }

                mirrorStore.updateMirrorShape(shapeStore.shapes[shapeIndex]);
              }
            }
          }
        });
      });
    },
  },
});
